import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  CircularProgress,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  Verified as VerifiedIcon,
  FilterList as FilterIcon,
  Info as InfoIcon,
  Clear as ClearIcon,
  People as PeopleIcon,
  Pending as PendingIcon,
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { api_urls } from "../../../utils/api-urls";

// Helper function to get full profile image URL
const getDocUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/')) return 'https://api.bharatdarshan.app' + path;
  return 'https://api.bharatdarshan.app/' + path;
};

const getProfileImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return 'https://api.bharatdarshan.app' + url;
};

// Helper function to get custom styling and label for plan names
const getPlanChipProps = (planName) => {
  if (!planName || planName.trim() === "" || planName.toLowerCase() === "not found" || planName.toLowerCase() === "no plan") {
    return {
      label: "Not Found",
      sx: {
        backgroundColor: "#e0e0e0",
        color: "#616161",
        fontWeight: "bold",
        fontSize: "0.75rem",
      },
    };
  }

  const normalized = planName.toLowerCase().trim();

  if (normalized.includes("bronze") || normalized.includes("basic")) {
    return {
      label: planName,
      sx: {
        backgroundColor: "#cd7f32",
        color: "#ffffff",
        fontWeight: "bold",
        fontSize: "0.75rem",
      },
    };
  }

  if (normalized.includes("silver") || normalized.includes("SILVER")) {
    return {
      label: planName,
      sx: {
        backgroundColor: "#78909c",
        color: "#ffffff",
        fontWeight: "bold",
        fontSize: "0.75rem",
      },
    };
  }

  if (normalized.includes("gold") || normalized.includes("GOLDEN")) {
    return {
      label: planName,
      sx: {
        backgroundColor: "#d97706",
        color: "#ffffff",
        fontWeight: "bold",
        fontSize: "0.75rem",
      },
    };
  }

  if (normalized.includes("platinum")) {
    return {
      label: planName,
      sx: {
        backgroundColor: "#6366f1",
        color: "#ffffff",
        fontWeight: "bold",
        fontSize: "0.75rem",
      },
    };
  }

  if (normalized.includes("diamond")) {
    return {
      label: planName,
      sx: {
        backgroundColor: "#06b6d4",
        color: "#ffffff",
        fontWeight: "bold",
        fontSize: "0.75rem",
      },
    };
  }

  if (normalized.includes("free")) {
    return {
      label: planName,
      sx: {
        backgroundColor: "#10b981",
        color: "#ffffff",
        fontWeight: "bold",
        fontSize: "0.75rem",
      },
    };
  }

  // Fallback for custom or unrecognized plan names
  return {
    label: planName,
    sx: {
      backgroundColor: "#e0e0e0",
      color: "#616161",
      fontWeight: "bold",
      fontSize: "0.75rem",
    },
  };
};


const MilanSathiUsers = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [premiumFilter, setPremiumFilter] = useState("");
  const [verificationFilter, setVerificationFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [verificationDocs, setVerificationDocs] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [userToDelete, setUserToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [notesUser, setNotesUser] = useState(null);
  const [notesText, setNotesText] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    totalUsers: 0,
    pendingVerifications: 0,
    premiumUsers: 0,
    totalTransactions: 0,
    totalRevenue: 0,
  });

  // Check URL params for filters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filter = params.get("filter");
    if (filter === "pending-verification") {
      setVerificationFilter("pending");
    } else if (filter === "premium") {
      setPremiumFilter("true");
    } else if (filter === "transactions") {
      navigate("/milan-sathi/transactions");
    }
  }, [location.search]);

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, [page, rowsPerPage, search, statusFilter, premiumFilter, verificationFilter, dateFilter, startDate, endDate]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${api_urls}api/admins/dashboard/stats`);
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      let url = `${api_urls}api/admins/users?page=${page + 1}&limit=${rowsPerPage}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (premiumFilter) url += `&isPremium=${premiumFilter}`;
      if (verificationFilter === "pending") {
        url += `&isVerificationComplete=true&isVerified=false`;
      } else if (verificationFilter === "verified") {
        url += `&isVerified=true`;
      }
      if (dateFilter === "today") {
        const today = new Date().toISOString().split("T")[0];
        url += `&startDate=${today}&endDate=${today}`;
      } else if (dateFilter === "week") {
        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
        url += `&startDate=${weekAgo}`;
      } else if (dateFilter === "month") {
        const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
        url += `&startDate=${monthAgo}`;
      } else if (dateFilter === "custom" && startDate && endDate) {
        url += `&startDate=${startDate}&endDate=${endDate}`;
      }

      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setUsers(data.data);
        setTotalUsers(data.pagination.total);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = async (user) => {
    setSelectedUser(user);
    setVerificationDocs(null);
    setDialogOpen(true);

    // Fetch verification documents for this user
    try {
      const response = await fetch(`${api_urls}api/admins/user-verification/${user._id}`);
      const data = await response.json();
      if (data.success && data.data) {
        setVerificationDocs(data.data);
      }
    } catch (error) {
      console.error("Error fetching verification docs:", error);
    }
  };

  const handleEditUser = (user) => {
    setEditData({
      _id: user._id,
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      status: user.status || "active",
      isPremium: user.isPremium || false,
      isVerified: user.isVerified || false,
      isDocumentVerified: user.isDocumentVerified || false,
    });
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleResetProfile = async (user) => {
    if (!window.confirm(`Are you sure you want to reset profile for ${user.firstName || user.phone}? This user will have to complete profile setup again.`)) return;
    try {
      const response = await fetch(`${api_urls}api/admins/users/${user._id}/reset`, {
        method: "POST",
      });
      const data = await response.json();
      if (data.success) {
        setSnackbar({ open: true, message: "Profile reset successfully!", severity: "success" });
        fetchUsers();
      } else {
        setSnackbar({ open: true, message: data.message || "Failed to reset profile", severity: "error" });
      }
    } catch (error) {
      console.error("Error resetting profile:", error);
      setSnackbar({ open: true, message: "Error resetting profile", severity: "error" });
    }
  };


  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      const response = await fetch(`${api_urls}api/admins/users/${userToDelete._id}`, {
        method: "DELETE",
      });
      const data = await response.json();
      if (data.success) {
        setSnackbar({ open: true, message: "User deleted successfully!", severity: "success" });
        fetchUsers();
        fetchStats();
      } else {
        setSnackbar({ open: true, message: data.message || "Failed to delete user", severity: "error" });
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      setSnackbar({ open: true, message: "Error deleting user", severity: "error" });
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleUpdateUser = async () => {
    try {
      const response = await fetch(`${api_urls}api/admins/users/${editData._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editData),
      });
      const data = await response.json();
      if (data.success) {
        setSnackbar({ open: true, message: "User updated successfully!", severity: "success" });
        fetchUsers();
        setEditDialogOpen(false);
      } else {
        setSnackbar({ open: true, message: data.message || "Failed to update user", severity: "error" });
      }
    } catch (error) {
      console.error("Error updating user:", error);
      setSnackbar({ open: true, message: "Error updating user", severity: "error" });
    }
  };

  const handleSaveNotes = async () => {
    if (!notesUser?._id) return;
    setNotesSaving(true);
    try {
      const response = await fetch(`${api_urls}api/admins/users/${notesUser._id}/admin-notes`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ adminNotes: notesText }),
      });
      const data = await response.json();
      if (data.success) {
        setSnackbar({ open: true, message: "Notes saved successfully!", severity: "success" });
        setNotesDialogOpen(false);
        setNotesUser(null);
        setNotesText("");
        fetchUsers();
      } else {
        setSnackbar({ open: true, message: data.message || "Failed to save notes", severity: "error" });
      }
    } catch (error) {
      console.error("Error saving notes:", error);
      setSnackbar({ open: true, message: "Error saving notes", severity: "error" });
    } finally {
      setNotesSaving(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setPremiumFilter("");
    setVerificationFilter("");
    setDateFilter("");
    setStartDate("");
    setEndDate("");
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const statCards = [
    {
      title: "Total Users",
      value: stats.totalUsers?.toLocaleString() || 0,
      icon: <PeopleIcon />,
      color: "#1976d2",
      onClick: () => clearFilters(),
    },
    {
      title: "Pending Verifications",
      value: stats.pendingVerifications?.toLocaleString() || 0,
      icon: <PendingIcon />,
      color: "#9c27b0",
      onClick: () => {
        clearFilters();
        setVerificationFilter("pending");
      },
    },
    {
      title: "Premium Users",
      value: stats.premiumUsers?.toLocaleString() || 0,
      icon: <StarIcon />,
      color: "#ed6c02",
      onClick: () => {
        clearFilters();
        setPremiumFilter("true");
      },
    },
    {
      title: "Total Transactions",
      value: stats.totalTransactions?.toLocaleString() || 0,
      icon: <ReceiptIcon />,
      color: "#0288d1",
      onClick: () => navigate("/milan-sathi/transactions"),
    },
    {
      title: "Total Revenue",
      value: `₹${stats.totalRevenue?.toLocaleString() || 0}`,
      icon: <MoneyIcon />,
      color: "#388e3c",
      onClick: () => navigate("/milan-sathi/transactions"),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: "bold" }}>
        Milan Sathi Users Management
      </Typography>

      {/* Stats Cards - Clickable */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((card, index) => (
          <Grid item xs={6} sm={4} md={2.4} key={index}>
            <Card
              sx={{
                cursor: "pointer",
                transition: "all 0.2s",
                border: "2px solid transparent",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: 3,
                  borderColor: card.color,
                },
              }}
              onClick={card.onClick}
            >
              <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box sx={{ color: card.color }}>{card.icon}</Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary" display="block">
                      {card.title}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: card.color }}>
                      {card.value}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <FilterIcon color="primary" />
          <Typography variant="h6">Filters</Typography>
          <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters} sx={{ ml: "auto" }}>
            Clear All
          </Button>
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "gray" }} /> }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Premium</InputLabel>
              <Select value={premiumFilter} label="Premium" onChange={(e) => setPremiumFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="false">Free</MenuItem>
                <MenuItem value="true">Premium</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Verification</InputLabel>
              <Select value={verificationFilter} label="Verification" onChange={(e) => setVerificationFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="verified">Verified</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Date</InputLabel>
              <Select value={dateFilter} label="Date" onChange={(e) => setDateFilter(e.target.value)}>
                <MenuItem value="">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">Last 7 Days</MenuItem>
                <MenuItem value="month">Last 30 Days</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {dateFilter === "custom" && (
            <>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Start Date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="End Date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Users Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell>User</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Package Type</TableCell>
                <TableCell>Verified</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">No users found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user._id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar src={getProfileImageUrl(user.profileImageUrl)} sx={{ width: 36, height: 36 }}>
                          {(user.firstName?.[0] || user.name?.[0] || "U").toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {user.firstName || user.lastName
                              ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
                              : user.name || "N/A"}
                          </Typography>
                          {user.isPremium && (
                            <Chip label="Premium" size="small" color="warning" sx={{ height: 18, fontSize: 10 }} />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{user.phone || "N/A"}</TableCell>
                    <TableCell>{user.email || "N/A"}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.status || "active"}
                        size="small"
                        color={user.status === "active" ? "success" : user.status === "suspended" ? "error" : "default"}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" {...getPlanChipProps(user.planName)} />
                    </TableCell>
                    <TableCell>
                      {user.isDocumentVerified ? (
                        <Chip label="Verified" size="small" color="success" icon={<VerifiedIcon />} />
                      ) : user.isVerificationComplete ? (
                        <Chip label="Pending" size="small" color="warning" />
                      ) : (
                        <Chip label="Not Applied" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>{formatDate(user.createdAt)}</TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            flex: 1,
                            color: user.adminNotes ? "#333" : "#bbb",
                            fontStyle: user.adminNotes ? "normal" : "italic",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={user.adminNotes || "No notes"}
                        >
                          {user.adminNotes || "No notes"}
                        </Typography>
                        <Tooltip title={user.adminNotes ? "Edit Notes" : "Add Notes"}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setNotesUser(user);
                              setNotesText(user.adminNotes || "");
                              setNotesDialogOpen(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Quick View">
                        <IconButton size="small" color="primary" onClick={() => handleViewUser(user)}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Full Details">
                        <IconButton size="small" color="success" onClick={() => navigate(`/milan-sathi/user-details/${user._id}`)}>
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" color="info" onClick={() => handleEditUser(user)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(user)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset Profile">
                        <IconButton size="small" color="warning" onClick={() => handleResetProfile(user)}>
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalUsers}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* View User Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>User Details</DialogTitle>
        <DialogContent dividers>
          {selectedUser && (
            <Grid container spacing={2}>
              <Grid item xs={12} display="flex" justifyContent="center">
                <Avatar src={getProfileImageUrl(selectedUser.profileImageUrl)} sx={{ width: 80, height: 80 }}>
                  {(selectedUser.firstName?.[0] || "U").toUpperCase()}
                </Avatar>
              </Grid>
              {[
                { label: "Name", value: `${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim() || "N/A" },
                { label: "Phone", value: selectedUser.phone || "N/A" },
                { label: "Email", value: selectedUser.email || "N/A" },
                { label: "Gender", value: selectedUser.gender || "N/A" },
                { label: "Age", value: selectedUser.age || "N/A" },
                { label: "City", value: selectedUser.city || "N/A" },
                { label: "State", value: selectedUser.state || "N/A" },
                { label: "Religion", value: selectedUser.religion || "N/A" },
                { label: "Profession", value: selectedUser.profession || "N/A" },
                { label: "Education", value: selectedUser.education || "N/A" },
                { label: "Marital Status", value: selectedUser.maritalStatus || "N/A" },
                { label: "Wallet", value: `₹${selectedUser.wallet || 0}` },
                { label: "Status", value: selectedUser.status || "active" },
                { label: "Premium", value: selectedUser.isPremium ? "Yes" : "No" },
                { label: "Document Verified", value: selectedUser.isDocumentVerified ? "Yes" : "No" },
                { label: "Joined", value: formatDate(selectedUser.createdAt) },
              ].map((item, i) => (
                <Grid item xs={6} key={i}>
                  <Typography variant="caption" color="textSecondary">{item.label}</Typography>
                  <Typography variant="body2" fontWeight="medium">{item.value}</Typography>
                </Grid>
              ))}

              {/* Verification Documents Section */}
              {verificationDocs && (
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold", borderTop: "1px solid #ddd", pt: 2 }}>
                    📄 Verification Documents
                  </Typography>
                  <Grid container spacing={2}>
                    {verificationDocs.panCard && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="textSecondary">PAN Card</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          {verificationDocs.panCard.includes('.') || verificationDocs.panCard.startsWith('uploads') ? (
                            <img
                              src={getDocUrl(verificationDocs.panCard)}
                              alt="PAN"
                              style={{ maxWidth: "100%", maxHeight: 150, borderRadius: 8, objectFit: "contain" }}
                            />
                          ) : (
                            <Chip label={verificationDocs.panCard} size="small" color="primary" />
                          )}
                        </Box>
                      </Grid>
                    )}
                    {verificationDocs.aadhaarCard && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="textSecondary">Aadhaar Card</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          {verificationDocs.aadhaarCard.includes('.') || verificationDocs.aadhaarCard.startsWith('uploads') ? (
                            <img
                              src={getDocUrl(verificationDocs.aadhaarCard)}
                              alt="Aadhaar"
                              style={{ maxWidth: "100%", maxHeight: 150, borderRadius: 8, objectFit: "contain" }}
                            />
                          ) : (
                            <Chip label={verificationDocs.aadhaarCard.substring(0, 4) + "****"} size="small" color="secondary" />
                          )}
                        </Box>
                      </Grid>
                    )}
                    {verificationDocs.verificationVideo && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary">Verification Video</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <video
                            src={getDocUrl(verificationDocs.verificationVideo)}
                            controls
                            style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8 }}
                          />
                        </Box>
                      </Grid>
                    )}
                    {!verificationDocs.panCard && !verificationDocs.aadhaarCard && !verificationDocs.verificationVideo && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">No documents uploaded</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                value={editData.firstName || ""}
                onChange={(e) => setEditData({ ...editData, firstName: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={editData.lastName || ""}
                onChange={(e) => setEditData({ ...editData, lastName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                value={editData.email || ""}
                onChange={(e) => setEditData({ ...editData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editData.status || "active"}
                  label="Status"
                  onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Premium</InputLabel>
                <Select
                  value={editData.isPremium ? "true" : "false"}
                  label="Premium"
                  onChange={(e) => setEditData({ ...editData, isPremium: e.target.value === "true" })}
                >
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Document Verified</InputLabel>
                <Select
                  value={editData.isDocumentVerified ? "true" : "false"}
                  label="Document Verified"
                  onChange={(e) => setEditData({ ...editData, isDocumentVerified: e.target.value === "true" })}
                >
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateUser}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user{" "}
            <strong>{userToDelete?.firstName || userToDelete?.phone || "this user"}</strong>?
          </Typography>
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteUser}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={notesDialogOpen} onClose={() => setNotesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Admin Notes — {notesUser?.firstName || notesUser?.name || notesUser?.phone || ""}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            maxRows={10}
            placeholder="Write notes about this user..."
            value={notesText}
            onChange={(e) => setNotesText(e.target.value)}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Only visible to admins.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesDialogOpen(false)} disabled={notesSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveNotes} disabled={notesSaving}>
            {notesSaving ? "Saving..." : "Save Notes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MilanSathiUsers;
